import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  Bell,
  Database,
  Brain,
  Save,
  CheckCircle,
  Webhook,
  Clock,
  Lock,
  Eye,
  EyeOff,
  AlertCircle
} from 'lucide-react'
import { changePassword, getConfig, getDataSourceStatus, updateStockPool } from '../api/client'

export default function Settings() {
  const [activeTab, setActiveTab] = useState('notifications')
  const [saved, setSaved] = useState(false)

  // 修改密码状态
  const [pwdForm, setPwdForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' })
  const [pwdShowOld, setPwdShowOld] = useState(false)
  const [pwdShowNew, setPwdShowNew] = useState(false)
  const [pwdLoading, setPwdLoading] = useState(false)
  const [pwdMessage, setPwdMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  // 股票池（从后端加载）
  const [stockPoolText, setStockPoolText] = useState('')
  const [poolLoading, setPoolLoading] = useState(true)
  const [poolSaveMsg, setPoolSaveMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null)
  const [poolSaving, setPoolSaving] = useState(false)
  const [dataStatus, setDataStatus] = useState<any | null>(null)
  const [dataStatusLoading, setDataStatusLoading] = useState(false)

  useEffect(() => {
    getConfig()
      .then((cfg) => {
        setStockPoolText(JSON.stringify(cfg.stock_pool_map || {}, null, 2))
      })
      .catch(() => {})
      .finally(() => setPoolLoading(false))
  }, [])

  useEffect(() => {
    if (activeTab !== 'data') return
    setDataStatusLoading(true)
    getDataSourceStatus()
      .then((resp) => setDataStatus(resp.data || null))
      .catch((err) => setDataStatus({
        data_source: 'akshare',
        status: 'error',
        last_success_at: null,
        last_error: err.message || '无法获取数据源状态'
      }))
      .finally(() => setDataStatusLoading(false))
  }, [activeTab])

  const handleSaveStockPool = async () => {
    let stockPoolMap: Record<string, string>
    try {
      stockPoolMap = JSON.parse(stockPoolText)
    } catch {
      setPoolSaveMsg({ type: 'err', text: '请输入合法的 JSON map' })
      return
    }
    if (!stockPoolMap || typeof stockPoolMap !== 'object' || Array.isArray(stockPoolMap)) {
      setPoolSaveMsg({ type: 'err', text: '股票池必须是代码到名称的映射' })
      return
    }
    const validEntries = Object.entries(stockPoolMap).filter(([code, name]) => /^\d{6}$/.test(code) && String(name).trim())
    if (validEntries.length === 0) { setPoolSaveMsg({ type: 'err', text: '至少输入一个有效的股票代码和名称' }); return }
    const normalizedMap = Object.fromEntries(validEntries.map(([code, name]) => [code, String(name).trim()]))
    try {
      setPoolSaving(true)
      const response = await updateStockPool(normalizedMap)
      setStockPoolText(JSON.stringify(response.stock_pool_map || normalizedMap, null, 2))
      setPoolSaveMsg({ type: 'ok', text: '股票池已保存' })
      setTimeout(() => setPoolSaveMsg(null), 3000)
    } catch (e: any) {
      setPoolSaveMsg({ type: 'err', text: e.message || '保存失败' })
    } finally {
      setPoolSaving(false)
    }
  }

  const [config, setConfig] = useState({
    // 通知设置
    dingtalkEnabled: true,
    dingtalkWebhook: 'https://oapi.dingtalk.com/robot/send?access_token=***',
    dingtalkSecret: '',
    feishuEnabled: false,
    feishuWebhook: '',
    
    // AI设置
    aiEnabled: true,
    aiApiKey: '',
    aiApiBase: 'https://api.openai.com/v1',
    aiModel: 'gpt-4',
    
    // 定时任务
    scheduleEnabled: true,
    scheduleDay: 5,
    scheduleTime: '15:30',
  })

  const handleSave = () => {
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  const handleChangePassword = async () => {
    setPwdMessage(null)
    if (!pwdForm.oldPassword || !pwdForm.newPassword) {
      setPwdMessage({ type: 'error', text: '请填写所有字段' })
      return
    }
    if (pwdForm.newPassword.length < 6) {
      setPwdMessage({ type: 'error', text: '新密码至少6位' })
      return
    }
    if (pwdForm.newPassword !== pwdForm.confirmPassword) {
      setPwdMessage({ type: 'error', text: '两次输入的新密码不一致' })
      return
    }
    try {
      setPwdLoading(true)
      await changePassword(pwdForm.oldPassword, pwdForm.newPassword)
      setPwdMessage({ type: 'success', text: '密码修改成功' })
      setPwdForm({ oldPassword: '', newPassword: '', confirmPassword: '' })
    } catch (err: any) {
      const msg = err.message?.includes('原密码错误') ? '原密码错误' : (err.message || '修改失败')
      setPwdMessage({ type: 'error', text: msg })
    } finally {
      setPwdLoading(false)
    }
  }

  const tabs = [
    { id: 'notifications', label: '通知设置', icon: Bell },
    { id: 'ai', label: 'AI分析', icon: Brain },
    { id: 'schedule', label: '定时任务', icon: Clock },
    { id: 'data', label: '数据源', icon: Database },
    { id: 'security', label: '账号安全', icon: Lock },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h2 className="text-2xl font-bold text-white">系统设置</h2>
        <p className="text-gray-400 mt-1">配置交易系统参数和集成服务</p>
      </motion.div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Tabs */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:w-64 space-y-2"
        >
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'bg-primary/20 text-primary border border-primary/30'
                    : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{tab.label}</span>
              </button>
            )
          })}
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex-1"
        >
          <div className="glass-card p-6">
            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Webhook className="w-5 h-5 text-primary" />
                  Webhook通知配置
                </h3>

                {/* DingTalk */}
                <div className="glass p-4 rounded-xl space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                        <Bell className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <h4 className="text-white font-medium">钉钉通知</h4>
                        <p className="text-gray-500 text-sm">通过钉钉机器人发送交易信号</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={config.dingtalkEnabled}
                        onChange={(e) => setConfig({ ...config, dingtalkEnabled: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>

                  {config.dingtalkEnabled && (
                    <div className="space-y-3 pt-4 border-t border-white/10">
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Webhook地址</label>
                        <input
                          type="text"
                          value={config.dingtalkWebhook}
                          onChange={(e) => setConfig({ ...config, dingtalkWebhook: e.target.value })}
                          className="w-full px-4 py-2 bg-dark border border-white/10 rounded-lg text-sm focus:outline-none focus:border-primary/50 transition-colors"
                          placeholder="https://oapi.dingtalk.com/robot/send?access_token=..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Secret密钥（可选）</label>
                        <input
                          type="password"
                          value={config.dingtalkSecret}
                          onChange={(e) => setConfig({ ...config, dingtalkSecret: e.target.value })}
                          className="w-full px-4 py-2 bg-dark border border-white/10 rounded-lg text-sm focus:outline-none focus:border-primary/50 transition-colors"
                          placeholder="SEC..."
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Feishu */}
                <div className="glass p-4 rounded-xl space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                        <Bell className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <h4 className="text-white font-medium">飞书通知</h4>
                        <p className="text-gray-500 text-sm">通过飞书机器人发送交易信号</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={config.feishuEnabled}
                        onChange={(e) => setConfig({ ...config, feishuEnabled: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>

                  {config.feishuEnabled && (
                    <div className="pt-4 border-t border-white/10">
                      <label className="block text-sm text-gray-400 mb-2">Webhook地址</label>
                      <input
                        type="text"
                        value={config.feishuWebhook}
                        onChange={(e) => setConfig({ ...config, feishuWebhook: e.target.value })}
                        className="w-full px-4 py-2 bg-dark border border-white/10 rounded-lg text-sm focus:outline-none focus:border-primary/50 transition-colors"
                        placeholder="https://open.feishu.cn/open-apis/bot/v2/hook/..."
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* AI Tab */}
            {activeTab === 'ai' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Brain className="w-5 h-5 text-primary" />
                  AI分析配置
                </h3>

                <div className="glass p-4 rounded-xl space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-white font-medium">启用AI分析</h4>
                      <p className="text-gray-500 text-sm">使用大模型进行深度分析</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={config.aiEnabled}
                        onChange={(e) => setConfig({ ...config, aiEnabled: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>

                  {config.aiEnabled && (
                    <div className="space-y-3 pt-4 border-t border-white/10">
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">API Key</label>
                        <input
                          type="password"
                          value={config.aiApiKey}
                          onChange={(e) => setConfig({ ...config, aiApiKey: e.target.value })}
                          className="w-full px-4 py-2 bg-dark border border-white/10 rounded-lg text-sm focus:outline-none focus:border-primary/50 transition-colors"
                          placeholder="sk-..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">API Base URL</label>
                        <input
                          type="text"
                          value={config.aiApiBase}
                          onChange={(e) => setConfig({ ...config, aiApiBase: e.target.value })}
                          className="w-full px-4 py-2 bg-dark border border-white/10 rounded-lg text-sm focus:outline-none focus:border-primary/50 transition-colors"
                          placeholder="https://api.openai.com/v1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">模型</label>
                        <select
                          value={config.aiModel}
                          onChange={(e) => setConfig({ ...config, aiModel: e.target.value })}
                          className="w-full px-4 py-2 bg-dark border border-white/10 rounded-lg text-sm focus:outline-none focus:border-primary/50 transition-colors"
                        >
                          <option value="gpt-4">GPT-4</option>
                          <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                          <option value="deepseek-chat">DeepSeek Chat</option>
                          <option value="claude-3-sonnet">Claude 3 Sonnet</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Schedule Tab */}
            {activeTab === 'schedule' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  定时任务配置
                </h3>

                <div className="glass p-4 rounded-xl space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-white font-medium">启用定时分析</h4>
                      <p className="text-gray-500 text-sm">每周自动执行股票分析</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={config.scheduleEnabled}
                        onChange={(e) => setConfig({ ...config, scheduleEnabled: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>

                  {config.scheduleEnabled && (
                    <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">执行日期</label>
                        <select
                          value={config.scheduleDay}
                          onChange={(e) => setConfig({ ...config, scheduleDay: parseInt(e.target.value) })}
                          className="w-full px-4 py-2 bg-dark border border-white/10 rounded-lg text-sm focus:outline-none focus:border-primary/50 transition-colors"
                        >
                          <option value={0}>周日</option>
                          <option value={1}>周一</option>
                          <option value={2}>周二</option>
                          <option value={3}>周三</option>
                          <option value={4}>周四</option>
                          <option value={5}>周五</option>
                          <option value={6}>周六</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">执行时间</label>
                        <input
                          type="time"
                          value={config.scheduleTime}
                          onChange={(e) => setConfig({ ...config, scheduleTime: e.target.value })}
                          className="w-full px-4 py-2 bg-dark border border-white/10 rounded-lg text-sm focus:outline-none focus:border-primary/50 transition-colors"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="glass p-4 rounded-xl">
                  <h4 className="text-white font-medium mb-3">股票池配置</h4>
                  {poolLoading ? (
                    <p className="text-gray-500 text-sm">加载中...</p>
                  ) : (
                    <>
                      <textarea
                        value={stockPoolText}
                        onChange={(e) => setStockPoolText(e.target.value)}
                        rows={8}
                        className="w-full px-4 py-2 bg-dark border border-white/10 rounded-lg text-sm focus:outline-none focus:border-primary/50 transition-colors font-mono"
                        placeholder='{"000001":"平安银行","600519":"贵州茅台"}'
                      />
                      <p className="text-gray-500 text-xs mt-2">股票池使用 JSON map，键为 6 位股票代码，值为股票名称。</p>
                      <div className="flex items-center gap-3 mt-3">
                        <button
                          onClick={handleSaveStockPool}
                          disabled={poolSaving}
                          className="flex items-center gap-2 px-4 py-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30 transition-colors text-sm font-medium disabled:opacity-50"
                        >
                          <Save className="w-4 h-4" />
                          {poolSaving ? '保存中...' : '保存股票池'}
                        </button>
                        {poolSaveMsg && (
                          <span className={`text-xs ${poolSaveMsg.type === 'ok' ? 'text-emerald-400' : 'text-red-400'}`}>
                            {poolSaveMsg.text}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Data Source Tab */}
            {activeTab === 'data' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Database className="w-5 h-5 text-primary" />
                  数据源状态
                </h3>

                <div className="glass p-4 rounded-xl space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">数据源</span>
                    <span className="text-white font-medium">AkShare</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">状态</span>
                    <span className={`font-medium ${dataStatus?.status === 'ok' ? 'text-emerald-400' : 'text-red-400'}`}>
                      {dataStatusLoading ? '检查中...' : dataStatus?.status === 'ok' ? '正常' : '异常'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">最近成功刷新</span>
                    <span className="text-white text-sm">{dataStatus?.last_success_at || '暂无'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">最近错误</span>
                    <span className="text-white text-sm">{dataStatus?.last_error || '无'}</span>
                  </div>
                </div>

                <div className={`glass p-4 rounded-xl border-l-4 ${dataStatus?.status === 'ok' ? 'border-emerald-500' : 'border-red-500'}`}>
                  <div className="flex items-start gap-3">
                    {dataStatus?.status === 'ok'
                      ? <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      : <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />}
                    <div>
                      <h4 className="text-white font-medium mb-1">AkShare</h4>
                      <p className="text-gray-400 text-sm">
                        {dataStatus?.status === 'ok'
                          ? '行情数据源可用'
                          : '行情数据源异常，请在后端运行诊断脚本确认依赖和网络'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Lock className="w-5 h-5 text-primary" />
                  修改密码
                </h3>

                <div className="glass p-4 rounded-xl space-y-4">
                  {/* 原密码 */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">原密码</label>
                    <div className="relative">
                      <input
                        type={pwdShowOld ? 'text' : 'password'}
                        value={pwdForm.oldPassword}
                        onChange={(e) => setPwdForm({ ...pwdForm, oldPassword: e.target.value })}
                        className="w-full px-4 py-2 pr-10 bg-dark border border-white/10 rounded-lg text-sm focus:outline-none focus:border-primary/50 transition-colors"
                        placeholder="请输入原密码"
                      />
                      <button
                        type="button"
                        onClick={() => setPwdShowOld(!pwdShowOld)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                      >
                        {pwdShowOld ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* 新密码 */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">新密码</label>
                    <div className="relative">
                      <input
                        type={pwdShowNew ? 'text' : 'password'}
                        value={pwdForm.newPassword}
                        onChange={(e) => setPwdForm({ ...pwdForm, newPassword: e.target.value })}
                        className="w-full px-4 py-2 pr-10 bg-dark border border-white/10 rounded-lg text-sm focus:outline-none focus:border-primary/50 transition-colors"
                        placeholder="至少6位"
                      />
                      <button
                        type="button"
                        onClick={() => setPwdShowNew(!pwdShowNew)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                      >
                        {pwdShowNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* 确认新密码 */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">确认新密码</label>
                    <input
                      type="password"
                      value={pwdForm.confirmPassword}
                      onChange={(e) => setPwdForm({ ...pwdForm, confirmPassword: e.target.value })}
                      className="w-full px-4 py-2 bg-dark border border-white/10 rounded-lg text-sm focus:outline-none focus:border-primary/50 transition-colors"
                      placeholder="再次输入新密码"
                    />
                  </div>

                  {/* 提示信息 */}
                  {pwdMessage && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                        pwdMessage.type === 'success'
                          ? 'bg-emerald-500/10 text-emerald-400'
                          : 'bg-red-500/10 text-red-400'
                      }`}
                    >
                      {pwdMessage.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                      {pwdMessage.text}
                    </motion.div>
                  )}

                  {/* 提交按钮 */}
                  <button
                    onClick={handleChangePassword}
                    disabled={pwdLoading}
                    className="flex items-center gap-2 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium disabled:opacity-50"
                  >
                    {pwdLoading ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Lock className="w-4 h-4" />
                    )}
                    {pwdLoading ? '修改中...' : '修改密码'}
                  </button>
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="flex items-center justify-between pt-6 border-t border-white/10">
              <div>
                {saved && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-center gap-2 text-emerald-400"
                  >
                    <CheckCircle className="w-5 h-5" />
                    <span>设置已保存</span>
                  </motion.div>
                )}
              </div>
              <button
                onClick={handleSave}
                className="flex items-center gap-2 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium"
              >
                <Save className="w-4 h-4" />
                保存设置
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
